import { roundCurrency } from "../Utility/Utility";

export const LINE_STATUS = {
  AVAILABLE: "available",
  OUT_OF_STOCK: "out_of_stock",
  UNAVAILABLE: "unavailable",
};

/* A line is unavailable when its product was not returned, is disabled or
   deprecated, or its denomination is disabled or deleted for the client.
   Out of stock follows checkout: the denomination is marked stockOut. */
export const getLineStatus = (line) => {
  const { details } = line;
  if (!details || !details.isAvailable) return LINE_STATUS.UNAVAILABLE;
  const denomination = details.denominations?.[line.denomination];
  if (denomination && !denomination.isAvailable) return LINE_STATUS.UNAVAILABLE;
  if (denomination?.stockOut) return LINE_STATUS.OUT_OF_STOCK;
  return LINE_STATUS.AVAILABLE;
};

export const isLinePurchasable = (line) => getLineStatus(line) === LINE_STATUS.AVAILABLE;

/* Payable price for one card, as checkout works it out: face value less the
   client's customer discount. */
export const getSellingPrice = (line) => {
  const denomination = Number(line?.denomination) || 0;
  const discount = Number(line?.details?.customerDiscount) || 0;
  return roundCurrency(denomination - (denomination * discount) / 100);
};

/* Builds the order summary from purchasable lines only.
   Fee per line = selling price x qty x fee% and GST is applied on that fee.
   The fee breakdown has one entry per product (its denominations added up),
   as the thank-you page shows it. Display only; checkout computes the final amount. */
export const calculateCartSummary = (lines) => {
  const summary = (lines || []).filter(isLinePurchasable).reduce(
    (result, line) => {
      const qty = Number(line.quantity) || 0;
      const denomination = Number(line.denomination) || 0;
      const sellingPrice = getSellingPrice(line);
      const feePercent = Number(line.details?.convenienceFeePercent) || 0;
      const gstPercent = Number(line.details?.gstPercent) || 0;

      const fee = (sellingPrice * qty * feePercent) / 100;
      const feeWithGst = roundCurrency(fee + (fee * gstPercent) / 100);

      result.totalQuantity += qty;
      result.totalDenomination += denomination * qty;
      result.totalDiscount += (denomination - sellingPrice) * qty;
      result.totalSellingPrice += sellingPrice * qty;
      result.convenienceFee += feeWithGst;

      if (feeWithGst > 0) {
        const key = line.sku || line.productId || line.lineId;
        const entry = result.feeBreakdown.find((item) => item.key === key);
        if (entry) {
          entry.amount = roundCurrency(entry.amount + feeWithGst);
        } else {
          result.feeBreakdown.push({
            key,
            productName: line.name || line.details?.name,
            feePercent,
            amount: feeWithGst,
          });
        }
      }
      return result;
    },
    {
      totalQuantity: 0,
      totalDenomination: 0,
      totalDiscount: 0,
      totalSellingPrice: 0,
      convenienceFee: 0,
      feeBreakdown: [],
    }
  );

  return {
    ...summary,
    totalDiscount: roundCurrency(summary.totalDiscount),
    convenienceFee: roundCurrency(summary.convenienceFee),
    totalAmount: roundCurrency(summary.totalSellingPrice + summary.convenienceFee),
  };
};
